/**
 * The arithmetic behind `seiri-time-stepper`, kept pure so it has a spec. Hours and minutes are whole numbers on a
 * 24-hour clock; nothing here knows about a date or a zone.
 */

export type TimeSegment = 'hours' | 'minutes'

/** The largest value a segment can be typed to. Steps only ever land on the 5-minute grid, so minutes stop at 55 there. */
export const SEGMENT_MAX: Record<TimeSegment, number> = { hours: 23, minutes: 59 }

/** Minutes step on this grid. */
export const MINUTE_STEP = 5

/** One step of `segment` up (`1`) or down (`-1`), wrapping at both ends. Hours move by one, 23 → 0 and 0 → 23. */
export const stepSegment = (segment: TimeSegment, value: number, direction: 1 | -1): number =>
  segment === 'hours' ? stepHours(value, direction) : stepMinutes(value, direction)

const stepHours = (hours: number, direction: 1 | -1): number => (hours + direction + 24) % 24

/**
 * Minutes move to the next or previous multiple of 5, so an off-grid value snaps onto it: 07 goes up to 10 and down to
 * 05, 10 goes up to 15 and down to 05. 55 wraps up to 00 and 00 down to 55. The hour is not carried.
 */
const stepMinutes = (minutes: number, direction: 1 | -1): number => {
  const next =
    direction === 1
      ? (Math.floor(minutes / MINUTE_STEP) + 1) * MINUTE_STEP
      : (Math.ceil(minutes / MINUTE_STEP) - 1) * MINUTE_STEP
  return (next + 60) % 60
}

/**
 * What a segment's text is worth: its digits as a number, or `null` while there are none, so an emptied input leaves
 * the value alone. `typeTime` never produces a text out of range, so nothing is clamped here.
 */
export const parseSegment = (text: string): number | null => {
  const digits = text.replace(/\D/g, '')
  return digits ? Number(digits) : null
}

/** The two segments' texts as typed, and the segment the caret is in. */
export interface TimeEntry {
  readonly hours: string
  readonly minutes: string
  readonly segment: TimeSegment
}

const SEPARATOR = /[:.,\s]/

/**
 * `typed` applied to `entry` one character at a time, as Maskito's time mask (Taiga UI's `InputTime`) does it:
 *
 * - A first digit that cannot start a two-digit value is padded at once: `3` in hours is `03`, `6` in minutes `06`.
 *   `0`–`2` in hours and `0`–`5` in minutes wait for a second digit.
 * - A digit that does not fit the hours **overflows** into the minutes, which start over from it: a third digit
 *   (`035` is `03` and `5`), or a second digit past 23 (`25` is `02` and `5`). The caret moves only then, so `03`
 *   stays in hours and can be backspaced.
 * - A separator (`:` `.` `,` or a space) in hours pads a single digit and moves to the minutes.
 * - A digit past full minutes, and anything else, is ignored.
 *
 * The returned `segment` is where the caret ends up.
 */
export const typeTime = (entry: TimeEntry, typed: string): TimeEntry => [...typed].reduce(typeCharacter, entry)

const typeCharacter = (entry: TimeEntry, character: string): TimeEntry => {
  if (/\d/.test(character)) return typeDigit(entry, character)
  if (SEPARATOR.test(character) && entry.segment === 'hours' && entry.hours) {
    return toMinutes(entry.hours.padStart(2, '0'))
  }
  return entry
}

const typeDigit = (entry: TimeEntry, digit: string): TimeEntry => {
  const { segment } = entry
  const text = entry[segment]
  const max = SEGMENT_MAX[segment]
  if (text.length === 0) {
    return withText(entry, Number(digit) > Math.floor(max / 10) ? `0${digit}` : digit)
  }
  if (text.length === 1 && Number(text + digit) <= max) return withText(entry, text + digit)
  if (segment === 'minutes') return entry
  return typeDigit(toMinutes(text.padStart(2, '0')), digit)
}

const withText = (entry: TimeEntry, text: string): TimeEntry =>
  entry.segment === 'hours' ? { ...entry, hours: text } : { ...entry, minutes: text }

const toMinutes = (hours: string): TimeEntry => ({ hours, minutes: '', segment: 'minutes' })

/** A segment as the stepper shows it: two digits, or `--` when there is no value. */
export const formatSegment = (value: number | null | undefined): string =>
  value === null || value === undefined ? '--' : String(value).padStart(2, '0')

/**
 * `base` with its time of day set to `hours:minutes`, as a new `Date`; `base` is not touched. The date part is
 * `base`'s, in the browser's zone. Seconds and milliseconds are zeroed.
 */
export const withTimeOfDay = (base: Date, hours: number, minutes: number): Date => {
  const result = new Date(base.getTime())
  result.setHours(hours, minutes, 0, 0)
  return result
}
